import androidx.compose.runtime.*
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*
import kotlinx.coroutines.launch

private fun algorithmLabel(value: String): String = when (value) {
    "berger_top_seeds_last" -> "1. a 2. nasazený v posledním kole"
    else -> "Standardní (Berger)"
}

@Composable
fun LeaguesScreen(
    token: String?,
    role: String?,
    seasonId: String,
    seasonName: String,
    onOpenTeams: (leagueId: String, leagueName: String) -> Unit,
) {
    var leagues by remember { mutableStateOf<List<SeasonLeagueDto>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var refreshKey by remember { mutableStateOf(0) }

    var name by remember { mutableStateOf("") }
    var teamCount by remember { mutableStateOf("8") }
    var tier by remember { mutableStateOf("") }
    var hasPlayoff by remember { mutableStateOf(false) }
    var scheduleAlgorithm by remember { mutableStateOf("berger") }
    var splitInfo by remember { mutableStateOf<String?>(null) }

    var editingLeagueId by remember { mutableStateOf<String?>(null) }
    var editTeamCount by remember { mutableStateOf("") }
    var editTier by remember { mutableStateOf("") }
    var editHasPlayoff by remember { mutableStateOf(false) }
    var editScheduleAlgorithm by remember { mutableStateOf("berger") }

    val scope = rememberCoroutineScope()

    LaunchedEffect(refreshKey) {
        loading = true
        try {
            leagues = Api.fetchLeagues(token, seasonId)
            error = null
        } catch (e: Throwable) {
            error = "Načtení lig selhalo: ${e.message}"
        }
        loading = false
    }

    Div(attrs = { classes("card") }) {
        H2 { Text("Ligy — sezóna $seasonName") }
        if (loading) Text("Načítám…")
        error?.let { Div { Text("Chyba: $it") } }
        splitInfo?.let { Div { Text(it) } }

        Table {
            Tr {
                Th { Text("Název") }
                Th { Text("Počet týmů") }
                Th { Text("Úroveň") }
                Th { Text("Nadstavba") }
                Th { Text("Algoritmus rozpisu") }
                if (role.canManage()) Th { }
            }
            leagues.forEach { league ->
                if (editingLeagueId == league.id) {
                    Tr {
                        Td { Text(league.name) }
                        Td {
                            Input(type = InputType.Text) {
                                value(editTeamCount)
                                onInput { editTeamCount = it.value }
                            }
                        }
                        Td {
                            Input(type = InputType.Text) {
                                value(editTier)
                                onInput { editTier = it.value }
                            }
                        }
                        Td {
                            Label {
                                CheckboxInput(checked = editHasPlayoff) { onInput { editHasPlayoff = it.value } }
                                Text(" Nadstavba")
                            }
                        }
                        Td {
                            Select(attrs = { onChange { editScheduleAlgorithm = it.value ?: "berger" } }) {
                                Option("berger", attrs = { if (editScheduleAlgorithm == "berger") selected() }) { Text("Standardní") }
                                Option("berger_top_seeds_last", attrs = { if (editScheduleAlgorithm == "berger_top_seeds_last") selected() }) { Text("1. a 2. nasazený v posledním kole") }
                            }
                        }
                        Td {
                            Button(attrs = {
                                onClick {
                                    val count = editTeamCount.toIntOrNull()
                                    if (count == null) {
                                        error = "Počet týmů musí být číslo"
                                    } else {
                                        scope.launch {
                                            try {
                                                Api.updateLeague(token, league.id, editHasPlayoff, count, editTier.toIntOrNull(), editScheduleAlgorithm)
                                                editingLeagueId = null
                                                refreshKey++
                                            } catch (e: Throwable) {
                                                error = "Uložení selhalo: ${e.apiMessage()}"
                                            }
                                        }
                                    }
                                }
                            }) { Text("Uložit") }
                            Button(attrs = { onClick { editingLeagueId = null } }) { Text("Zrušit") }
                        }
                    }
                } else {
                    Tr {
                        Td(attrs = { onClick { onOpenTeams(league.id, league.name) } }) { Text(league.name) }
                        Td { Text(league.teamCount.toString()) }
                        Td { Text(league.tier?.toString() ?: "—") }
                        Td { Text(if (league.hasPlayoff) "Ano" else "Ne") }
                        Td { Text(algorithmLabel(league.scheduleAlgorithm)) }
                        if (role.canManage()) {
                            Td {
                                Button(attrs = {
                                    onClick {
                                        editingLeagueId = league.id
                                        editTeamCount = league.teamCount.toString()
                                        editTier = league.tier?.toString() ?: ""
                                        editHasPlayoff = league.hasPlayoff
                                        editScheduleAlgorithm = league.scheduleAlgorithm
                                    }
                                }) { Text("Upravit") }
                                if (league.hasPlayoff) {
                                    Button(attrs = {
                                        onClick {
                                            scope.launch {
                                                try {
                                                    val result = Api.splitPlayoff(token, league.id)
                                                    splitInfo =
                                                        "Nadstavba rozdělena — O titul: ${result.titleGroup.joinToString(", ")}; " +
                                                            "O udržení: ${result.relegationGroup.joinToString(", ")}"
                                                    error = null
                                                } catch (e: Throwable) {
                                                    error = "Rozdělení selhalo: ${e.apiMessage()}"
                                                }
                                            }
                                        }
                                    }) { Text("Rozdělit do nadstavby") }
                                }
                            }
                        }
                    }
                }
            }
        }

        if (role.canManage()) {
            H3 { Text("Nová liga") }
            Div {
                Input(type = InputType.Text) {
                    placeholder("Název, např. 1. liga")
                    value(name)
                    onInput { name = it.value }
                }
                Input(type = InputType.Text) {
                    placeholder("Počet týmů")
                    value(teamCount)
                    onInput { teamCount = it.value }
                }
                Input(type = InputType.Text) {
                    placeholder("Úroveň (1 = nejvyšší, nepovinné)")
                    value(tier)
                    onInput { tier = it.value }
                }
                Label {
                    CheckboxInput(checked = hasPlayoff) { onInput { hasPlayoff = it.value } }
                    Text(" Nadstavba (rozdělení na O titul / O udržení po podzimu a jaru)")
                }
                Select(attrs = { onChange { scheduleAlgorithm = it.value ?: "berger" } }) {
                    Option("berger", attrs = { if (scheduleAlgorithm == "berger") selected() }) { Text("Standardní algoritmus rozpisu") }
                    Option("berger_top_seeds_last", attrs = { if (scheduleAlgorithm == "berger_top_seeds_last") selected() }) { Text("1. a 2. nasazený v posledním kole") }
                }
                Button(attrs = {
                    onClick {
                        val count = teamCount.toIntOrNull()
                        if (count == null) {
                            error = "Počet týmů musí být číslo"
                        } else {
                            scope.launch {
                                try {
                                    Api.createLeague(token, seasonId, name, count, hasPlayoff, tier.toIntOrNull(), scheduleAlgorithm)
                                    name = ""
                                    teamCount = "8"
                                    tier = ""
                                    hasPlayoff = false
                                    scheduleAlgorithm = "berger"
                                    refreshKey++
                                } catch (e: Throwable) {
                                    error = "Založení selhalo: ${e.message}"
                                }
                            }
                        }
                    }
                }) { Text("Založit ligu") }
            }
        }
    }
}
